<script setup lang="ts">
    import { computed, toRef } from 'vue'
    import type { UserReadModel } from '@Alternatives/Assignments/Domain/readmodels'
    import { AssignUsersEventPayload } from '@Alternatives/Assignments/Components/UsersAssignerDialog'
    import type { UsersAssignerDialogViewModel } from '@Alternatives/Assignments/Components/UsersAssignerDialog'


    // ---------------- props / emits ----------------

    const props = defineProps<{
        viewModel: UsersAssignerDialogViewModel
    }>()

    const emit = defineEmits<{
        (e: 'assign-users', payload: AssignUsersEventPayload): void
    }>()

    // ---------------- shortcuts ----------------

    const vm = toRef(props, 'viewModel')

    // ---------------- derived ----------------

    const dialogTitle = computed(() => vm.value.dialogTitle)

    // filters (writable, v-model targets)
    const searchPhrase = computed<string | null>({
        get: () => vm.value.searchPhrase,
        set: val => { vm.value.searchPhrase = val }
    })

    const tag = computed<string | null>({
        get: () => vm.value.tag,
        set: val => { vm.value.tag = val }
    })

    const usersView = computed<UserReadModel[]>(() => vm.value.usersView ?? [])
    const tags = computed(() => vm.value.tags)

    // selection helpers
    const noneSelected = computed(() => !vm.value.users.some(u => u.IsUiSelected))
    const allSelected = computed(() => !vm.value.users.some(u => !u.IsUiSelected))
    const anySelected = computed(() => vm.value.users.some(u => u.IsUiSelected))
    const selected = computed(() => vm.value.users.filter(u => u.IsUiSelected))

    // ---------------- methods ----------------

    function clearFilters()
    {
        tag.value = null
        searchPhrase.value = null
    }

    function selectAll()
    {
        usersView.value.forEach(u => u.IsUiSelected = true)
    }

    function unselectAll()
    {
        vm.value.users.forEach(u => u.IsUiSelected = false)
    }

    function handleAssign()
    {
        emit(
            'assign-users',
            new AssignUsersEventPayload(
                vm.value.alternativeIds,
                selected.value.map(u => u.Id)
            )
        )
    }

    function close()
    {
        vm.value.close()
    }
</script>


<template>
    <div class="modal-mask" v-if="!vm.closed">
        <div class="modal-wrapper">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">{{dialogTitle}}</h5>
                        <button type="button" class="close" @click="close">&times;</button>
                    </div>

                    <div class="modal-body">
                        <div style="min-height: 400px;">
                            <nav class="navbar navbar-light navbar-expand-md bg-light" role="navigation">

                                <div class="dropdown ml-auto">
                                    <button class="btn btn-sm dropdown-toggle d-flex align-items-center"
                                            :class="tag ? 'btn-info' : 'btn-outline-secondary'"
                                            type="button"
                                            data-toggle="dropdown">
                                        <i class="fas fa-filter mr-1"
                                           :class="tag ? 'text-warning' : 'text-muted'">
                                        </i>
                                        <span>Tag</span>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right dropdown-menu-scrollable">
                                        <a class="dropdown-item"
                                           :class="{ 'active': tag === null }"
                                           @click="tag = null">
                                            None
                                        </a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item"
                                           v-for="t in tags"
                                           :key="t"
                                           :class="{ 'active': tag === t }"
                                           @click="tag = t">
                                            {{ t }}
                                        </a>
                                    </div>
                                </div>

                                <form class="form-inline my-2 my-lg-0 ml-sm-2" role="search">
                                    <input class="form-control form-control-sm" type="search" placeholder="Search" v-model="searchPhrase">
                                </form>
                            </nav>

                            <div v-if="tag || searchPhrase" class="alert alert-info alert-dismissible fade show d-flex align-items-center mt-3" role="alert">
                                <i class="fas fa-info-circle mr-2"></i>
                                <span>
                                    Showing users
                                    <span v-if="tag">filtered by tag: <strong>{{ tag }}</strong></span>
                                    <span v-if="tag && searchPhrase"> and </span>
                                    <span v-if="searchPhrase">matching "<strong>{{ searchPhrase }}</strong>"</span>.
                                </span>
                                <button type="button" class="close ml-auto" @click="clearFilters">
                                    <span>&times;</span>
                                </button>
                            </div>

                            <table class="table table-bordered">
                                <thead>
                                    <tr>
                                        <th style="width: 40px; min-width: 40px !important;">
                                            <div class="btn-group">
                                                <a class="pointer" v-if="noneSelected" @click="selectAll"><i class="fa-regular fa-square"></i></a>
                                                <a class="pointer" v-if="anySelected && !allSelected" @click="unselectAll"><i class="fa fa-minus-square"></i></a>
                                                <a class="pointer" v-if="anySelected && allSelected" @click="unselectAll"><i class="fa-solid fa-square-check"></i></a>
                                            </div>
                                        </th>
                                        <th style="width: 300px;">User</th>
                                        <th>Tags</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="u in usersView" :key="u.Id">
                                        <td><input type="checkbox" v-model="u.IsUiSelected" /></td>

                                        <td>
                                            <p class="fg-color-gray mb-0 mt-1">
                                                <span>{{u.Name}}</span>
                                            </p>
                                            <small class="text-muted">{{u.Email}}</small>
                                        </td>
                                        <td>
                                            <span class="badge mr-1 pointer"
                                                  :class="tag === t ? 'badge-info' : 'badge-secondary'"
                                                  v-for="t in u.Tags"
                                                  :key="t"
                                                  @click="tag = t">
                                                {{ t }}
                                            </span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div class="modal-footer">
                        <button type="button" @click="close" class="btn btn-outline-secondary">Cancel</button>
                        <button type="button" :disabled="selected.length === 0" @click="handleAssign" class="btn btn-primary">Assign ({{selected.length}})</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>